import { Component, computed, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { AssessmentService } from '../../services/assessment.service';
import { EmployerDetails } from '../../data/employer-details';
import { IFormComponent } from '../../models/i-form-component';
import { AbnValidators } from '../../validators/abn-validatiors';
import { OptionItem } from '../../data/option-item';

/**
 * Same object (@type {EmployerDetails}) as @type {EmployerDetailsComponent}
 * However the layout is different enough that it seemed cleaner to just separate the components,
 * rather than doing conditional spaghetti
 */
@Component({
  selector: 'app-employer-sponsor',
  templateUrl: './employer-sponsor.component.html',
  styleUrl: './employer-sponsor.component.scss'
})
export class EmployerSponsorComponent implements OnInit, IFormComponent {
  
  assessmentService = inject(AssessmentService);
  formBuilder = inject(NonNullableFormBuilder);
  isVevo!: boolean;
  

  @Input()model!: InitialAssessmentForm;
  @Input()phoneTypes!: OptionItem[];
  @Output() formReady = new EventEmitter<FormGroup>();

  employerSponsorForm = this.formBuilder.group({
    name: ['', [Validators.required]],
    abn: ['', [AbnValidators.maxLength()]],
    firstName: ['', [Validators.required]],
    surname: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phoneType: [null as OptionItem | null],
    phoneCountryCode: [''],
    phoneAreaCode: [''],
    phoneNumber: ['', [Validators.min(0)]],
    // Field names differ from actual model properties,
    // for address component compatibility.
    businessAddress: this.formBuilder.group({
      streetAddress: [''],
      streetAddress2: [''],
      suburb: [''],
      city: [''],
      postcode: [''],
      state: [''],
      country: [''],
    }),    
    startTrading: [''],
    ausStaff: ['', Validators.min(0)],
    investedTraining: ['']
    });
      
  ngOnInit(): void {    

    this.isVevo = this.assessmentService.isVevo();

    this.handleFormModelBinding();

    this.formReady.emit(this.employerSponsorForm);
  }

  handleFormModelBinding() {
    
    this.model = this.model || new InitialAssessmentForm();
    this.model.employer = this.model.employer || new EmployerDetails();
    
    // Bind form to model data
    this.employerSponsorForm?.patchValue(this.model.employer);   
    
    // Subscribe to form changes to update model
    this.employerSponsorForm?.valueChanges.subscribe(value => {
      this.model.employer = {
        ...this.model.employer!,
        ...value,
        // Flatten and map address fields back to model structure
        ...value.businessAddress,
        address1: value.businessAddress!.streetAddress!,
        address2: value.businessAddress!.streetAddress2!
      }
    });
  }

  // Might be worth moving to a pipe if used elsewhere
  // Its being called frequently due to Angular change detection
  personTerm(suffix: string = '') : string {
    const term = 
      this.assessmentService.isVevoEmployee()
      ? 'Contact Person'
      : 'Employer Contact Person';
    return suffix ? `${term} ${suffix}` : term;    
  }


  isFormValid(): boolean {
    return this.employerSponsorForm.valid;
  }

  markFormAsTouched(): void {
    this.employerSponsorForm.markAllAsTouched();
  }

}
